import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowUp, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, fileUrl } from "../lib/api";
import PlotReveal from "../components/PlotReveal";
import WavyEnd from "../components/WavyEnd";

const EASE = [0.16, 1, 0.3, 1];

async function shareProject(p) {
  const url = `${API}/share/${p.slug}`;
  const touch = window.matchMedia("(pointer: coarse)").matches;
  if (touch && navigator.share) {
    try {
      await navigator.share({ title: `${p.title} — TWP`, url });
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't open sharing.");
    }
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied");
  } catch {
    window.prompt("Copy this share link", url);
  }
}

function heroStyle(p) {
  const fx = p?.hero_focal_x ?? 50, fy = p?.hero_focal_y ?? 50;
  const scale = (p?.hero_scale ?? 100) / 100;
  return { objectFit: "cover", objectPosition: `${fx}% ${fy}%`, transform: `scale(${scale})`, transformOrigin: `${fx}% ${fy}%` };
}

/** Sleek arrow — architectural thin line */
function Arrow({ direction = "right", className = "" }) {
  const rotate = { right: 0, left: 180, up: -90, down: 90 }[direction] || 0;
  return (
    <svg width="44" height="14" viewBox="0 0 44 14" className={className} style={{ transform: `rotate(${rotate}deg)` }}>
      <line x1="0" y1="7" x2="40" y2="7" stroke="currentColor" strokeWidth="1" />
      <line x1="34" y1="1" x2="42" y2="7" stroke="currentColor" strokeWidth="1" />
      <line x1="34" y1="13" x2="42" y2="7" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const { data: p, isLoading, error } = useQuery({
    queryKey: ["project", slug],
    queryFn: async () => (await api.get(`/projects/${slug}`)).data,
  });

  const [lightboxIdx, setLightboxIdx] = useState(null); // index in gallery when open
  const [showTop, setShowTop] = useState(false);         // scroll-to-top button visibility
  const gallery = p?.gallery || [];
  const orderedGallery = gallery; // already sorted by admin order

  // Metadata rows for the aside — memoised so we don't rebuild + filter on
  // every render. Small array, negligible saving, but keeps the JSX readable.
  const metaRows = useMemo(() => (
    [
      ["Location", p?.location],
      ["Year", p?.year],
      ["Status", p?.status],
      ["Scope", p?.scope],
    ].filter(([, v]) => v)
  ), [p?.location, p?.year, p?.status, p?.scope]);

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const next = useCallback(() => {
    setLightboxIdx((i) => (i == null ? i : (i + 1) % orderedGallery.length));
  }, [orderedGallery.length]);
  const prev = useCallback(() => {
    setLightboxIdx((i) => (i == null ? i : (i - 1 + orderedGallery.length) % orderedGallery.length));
  }, [orderedGallery.length]);

  // Show the scroll-to-top button once the user is past the first viewport.
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // Enable page-level scroll-snap only while ProjectDetail is mounted so the
  // gallery snaps one plate per scroll gesture without affecting other pages.
  useEffect(() => {
    document.documentElement.classList.add("project-scroll-snap");
    return () => document.documentElement.classList.remove("project-scroll-snap");
  }, []);

  // Custom smooth "one stop per gesture" scroll.
  // Native CSS mandatory snap fights `scroll-behavior: smooth` on user wheel
  // events, so we intercept the wheel and animate to the NEXT / PREV snap
  // target using window.scrollTo({ behavior: "smooth" }). Debounced so a
  // single trackpad flick can't skip past a stop.
  //
  // Snap targets include BOTH images ([data-snap="plate"]) and text
  // sections ([data-snap="section"] — the metadata header, project body).
  // Images centre in the visible area *below* the fixed site header;
  // text sections snap to their top with header-offset so their heading
  // doesn't slip under the header.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let cooling = false;
    const COOLDOWN_MS = 700;
    const WHEEL_THRESHOLD = 12;

    // The site header is fixed `h-24 md:h-32` (96 / 128 px). Keep a small
    // buffer so the image doesn't hug the header edge.
    const headerH = () => (window.innerWidth >= 768 ? 128 : 96) + 24;

    const getTargets = () => Array.from(document.querySelectorAll('[data-snap="plate"], [data-snap="section"]'));
    const kind = (el) => el.getAttribute("data-snap");
    const rectTop = (el) => el.getBoundingClientRect().top + window.scrollY;

    // scrollY that snaps `el` into position.
    const snapY = (el) => {
      const top = rectTop(el);
      const h = el.offsetHeight;
      const vh = window.innerHeight;
      const hdr = headerH();
      if (kind(el) === "plate") {
        // Centre the image in the space below the header.
        const availableCenter = hdr + (vh - hdr) / 2;
        return Math.max(0, top + h / 2 - availableCenter);
      }
      // Section: align its top just below the header.
      return Math.max(0, top - hdr);
    };

    const advance = (dir) => {
      const list = getTargets();
      if (!list.length) return false;
      const currY = window.scrollY;
      // Sort targets by snapY position.
      const sorted = list
        .map((el) => ({ el, y: snapY(el) }))
        .sort((a, b) => a.y - b.y);
      // Find current index — the target whose snapY is closest to current
      // scroll position.
      let currIdx = 0;
      let dist = Infinity;
      sorted.forEach((t, i) => {
        const d = Math.abs(t.y - currY);
        if (d < dist) { dist = d; currIdx = i; }
      });
      // If we're not aligned to any target (dist > ~small threshold), snap
      // to the CLOSEST target in the intended direction first.
      const aligned = dist < 40;
      let targetIdx;
      if (!aligned) {
        const inDir = sorted
          .map((t, i) => ({ ...t, i }))
          .filter(({ y }) => dir > 0 ? y > currY + 10 : y < currY - 10);
        if (inDir.length) {
          inDir.sort((a, b) => dir > 0 ? a.y - b.y : b.y - a.y);
          targetIdx = inDir[0].i;
        } else {
          targetIdx = currIdx;
        }
      } else {
        targetIdx = currIdx + dir;
      }
      if (targetIdx < 0 || targetIdx >= sorted.length) return false;
      const targetY = sorted[targetIdx].y;
      window.scrollTo({ top: targetY, behavior: "smooth" });
      return true;
    };

    const onWheel = (e) => {
      if (Math.abs(e.deltaY) < WHEEL_THRESHOLD) return;
      // Release control past the last snap target (footer / continue block).
      const list = getTargets();
      if (list.length) {
        const lastY = Math.max(...list.map(snapY));
        if (e.deltaY > 0 && window.scrollY > lastY + 40) return;
      }
      if (cooling) { e.preventDefault(); return; }
      if (advance(e.deltaY > 0 ? 1 : -1)) {
        e.preventDefault();
        cooling = true;
        setTimeout(() => { cooling = false; }, COOLDOWN_MS);
      }
    };

    const onKey = (e) => {
      const down = ["ArrowDown", "PageDown", " "].includes(e.key);
      const up = ["ArrowUp", "PageUp"].includes(e.key);
      if (!down && !up) return;
      if (cooling) return;
      if (advance(down ? 1 : -1)) {
        e.preventDefault();
        cooling = true;
        setTimeout(() => { cooling = false; }, COOLDOWN_MS);
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
    };
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightboxIdx == null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, next, prev]);

  if (isLoading) return <div className="pt-40 container-w mono-label">Loading…</div>;
  if (error || !p) return (
    <div className="pt-40 container-w">
      <div className="mono-label mb-4">Not found</div>
      <h1 className="font-display italic text-5xl">This project has not been published.</h1>
      <Link to="/work" className="mono-label link-underline mt-8 inline-block">← Back to work</Link>
    </div>
  );

  const hero = orderedGallery.find((g) => g.id === p.hero_image_id) || orderedGallery[0];
  const galleryRest = orderedGallery.filter((g) => g.id !== p.hero_image_id);
  const isVideo = p.hero_media_type === "video" && p.hero_video_path;

  return (
    <article data-testid="project-detail" className="pt-28 md:pt-36">
      {/* Metadata Header */}
      <header className="container-w mb-16 md:mb-24 relative" data-snap="section">
        <div className="mono-label mb-6">
          <Link to="/work" className="link-underline">← Work</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="mono-label mb-6">{p.discipline}</div>
            <h1 className="font-display italic text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight">{p.title}</h1>
            {p.summary && (
              <>
                <p className="mt-8 md:mt-12 text-lg md:text-xl text-ash max-w-2xl leading-relaxed">{p.summary}</p>
                <WavyEnd className="text-marine" width={180} />
              </>
            )}
          </div>
          <aside className="md:col-span-4 md:pl-12 md:border-l md:border-ink/10 pt-8 md:pt-4">
            <dl className="space-y-6">
              {metaRows.map(([k, v]) => (
                <div key={k}>
                  <dt className="mono-label mb-1">{k}</dt>
                  <dd className="text-base">{v}</dd>
                </div>
              ))}
            </dl>
            <button
              type="button"
              onClick={() => shareProject(p)}
              className="mt-10 mono-label inline-flex items-center gap-2 link-underline text-marine"
              data-testid="project-share-button"
            >
              <Share2 size={14} strokeWidth={1.5} /> Share project
            </button>
          </aside>
        </div>
      </header>

      {/* Hero media (image or video) — sized to the image's own aspect ratio,
          capped by 92vh so ultra-tall portraits don't dominate the viewport.
          When hero_scale > 100 the image is intentionally zoomed and the
          extra bleed is clipped by overflow-hidden using the focal anchor. */}
      {(hero || isVideo) && (() => {
        const w = hero?.width || 16;
        const h = hero?.height || 9;
        const ar = w / h;
        const wrapperStyle = {
          aspectRatio: `${w} / ${h}`,
          // Cap by viewport height minus the fixed site header + a little
          // breathing room, so the top of the image never slips behind the
          // header when the snap centres it.
          maxHeight: "78vh",
          width: `min(100%, calc(78vh * ${ar}))`,
          margin: "0 auto",
        };
        return (
          <section className="container-w mb-24 md:mb-40" data-testid="project-hero">
            <PlotReveal>
              <div className="border border-greige/60 p-2 md:p-3 bg-paper" style={wrapperStyle} data-snap="plate">
                <div className="overflow-hidden w-full h-full">
                  {isVideo ? (
                    <video src={fileUrl(p.hero_video_path)} autoPlay muted loop playsInline
                      className="w-full h-full" style={heroStyle(p)} />
                  ) : hero ? (
                    <img src={fileUrl(hero.variants.full)} alt={hero.alt}
                      className="w-full h-full" style={heroStyle(p)} loading="eager" />
                  ) : null}
                </div>
              </div>
              {hero?.caption && <div className="mono-label mt-3">{hero.caption}</div>}
            </PlotReveal>
          </section>
        );
      })()}

      {/* Body — proper column, no more overlapping label */}
      {p.body && (
        <section className="container-w mb-32 md:mb-40" data-snap="section">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-3">
              <div className="mono-label sticky top-24">— On this project / 02</div>
            </div>
            <div className="md:col-span-8 md:col-start-4 font-display italic text-base md:text-lg leading-[1.5] whitespace-pre-wrap text-ink max-w-2xl">
              {p.body}
            </div>
          </div>
        </section>
      )}

      {/* Gallery — clickable grid, opens lightbox */}
      {galleryRest.length > 0 && (
        <section className="container-w mb-32 md:mb-48">
          <div className="flex items-baseline justify-between mb-12">
            <div className="mono-label">Sequence · {orderedGallery.length} plates</div>
            <button
              onClick={() => openLightbox(0)}
              className="mono-label link-underline"
              data-testid="open-lightbox"
            >
              Enter sequence →
            </button>
          </div>
          <div className="space-y-24 md:space-y-40">
            {galleryRest.map((img, idx) => {
              const globalIdx = orderedGallery.findIndex((g) => g.id === img.id);
              // Each plate is sized to its own aspect ratio, capped at 85vh so
              // one image fits within the viewport (no long-scroll giants).
              const w = img.width || 4;
              const h = img.height || 3;
              const ar = w / h;
              const plateStyle = {
                aspectRatio: `${w} / ${h}`,
                maxHeight: "72vh",
                width: `min(100%, calc(72vh * ${ar}))`,
              };
              // Alternating alignment keeps the editorial rhythm without
              // changing image size.
              const alignClass = idx % 3 === 1 ? "md:ml-auto md:mr-0" : idx % 3 === 2 ? "md:mx-auto" : "";
              return (
                <PlotReveal key={img.id}>
                  <figure
                    className={`${alignClass}`}
                    style={plateStyle}
                    data-snap="plate"
                    data-testid={`gallery-plate-${idx}`}
                  >
                    <button
                      onClick={() => openLightbox(globalIdx)}
                      className="block w-full h-full border border-greige/60 p-2 md:p-3 bg-paper"
                      data-cursor="hover"
                      data-testid={`gallery-thumb-${idx}`}
                    >
                      <div className="overflow-hidden w-full h-full">
                        <motion.img
                          src={fileUrl(img.variants.full)}
                          alt={img.alt}
                          className="w-full h-full object-cover block"
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.9, ease: EASE }}
                          loading="lazy"
                        />
                      </div>
                    </button>
                    <figcaption className="flex justify-between items-baseline mt-3 mono-label">
                      <span>{String(globalIdx + 1).padStart(2, "0")} / {String(orderedGallery.length).padStart(2, "0")}</span>
                      <span>{img.caption}</span>
                    </figcaption>
                  </figure>
                </PlotReveal>
              );
            })}
          </div>
        </section>
      )}

      {/* Next / footer */}
      <section className="container-w border-t border-ink/15 py-24 md:py-32" data-snap="section">
        <div className="mono-label mb-4">Continue</div>
        <div className="flex items-baseline justify-between flex-wrap gap-6">
          <Link to="/work" className="font-display italic text-4xl md:text-6xl link-underline" data-testid="continue-work">All projects →</Link>
          <Link to="/contact" className="mono-label link-underline">Start an enquiry →</Link>
        </div>
      </section>

      {/* ---------- Lightbox (Sheet Transition sequence) ---------- */}
      <AnimatePresence>
        {lightboxIdx != null && orderedGallery[lightboxIdx] && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-50 bg-ink"
            data-testid="lightbox"
          >
            {/* Top bar */}
            <div className="absolute top-0 left-0 right-0 h-14 md:h-16 flex items-center justify-between px-6 md:px-10 text-paper mono-label z-10">
              <span>{p.title} · Plate {String(lightboxIdx + 1).padStart(2, "0")} / {String(orderedGallery.length).padStart(2, "0")}</span>
              <button onClick={closeLightbox} className="link-underline" data-testid="lightbox-close">Close ✕</button>
            </div>
            {/* Image (with room for thumbnail rail on md+) */}
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={orderedGallery[lightboxIdx].id}
                src={fileUrl(orderedGallery[lightboxIdx].variants.full)}
                alt={orderedGallery[lightboxIdx].alt}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.55, ease: EASE }}
                className="absolute top-16 bottom-12 left-4 right-4 md:right-32 m-auto max-w-[92%] max-h-[80%] object-contain"
              />
            </AnimatePresence>

            {/* Thumbnail rail — right side, click to jump */}
            <aside
              className="hidden md:flex absolute right-0 top-16 bottom-0 w-28 flex-col border-l border-paper/15 bg-ink/95 overflow-y-auto py-4 z-10"
              aria-label="Jump to image"
              data-testid="lightbox-thumbs"
            >
              {orderedGallery.map((im, i) => {
                const isActive = i === lightboxIdx;
                return (
                  <button
                    key={im.id}
                    type="button"
                    onClick={() => setLightboxIdx(i)}
                    aria-label={`Show image ${i + 1}`}
                    aria-current={isActive}
                    className={`mx-3 mb-3 shrink-0 block transition-opacity duration-500 ${isActive ? "opacity-100" : "opacity-55 hover:opacity-90"}`}
                    data-testid={`lightbox-thumb-${i}`}
                  >
                    <div className={`w-full aspect-[3/4] overflow-hidden bg-ink border ${isActive ? "border-paper" : "border-paper/20 hover:border-paper/50"}`}>
                      <img src={fileUrl(im.variants.thumb || im.variants.grid)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className={`mono-label text-[9px] mt-1 text-right ${isActive ? "text-paper" : "text-paper/50"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </button>
                );
              })}
            </aside>
            {/* Prev / Next arrows */}
            <button
              onClick={prev}
              data-testid="lightbox-prev"
              className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 text-paper hover:text-mist p-4 flex items-center gap-3 mono-label z-20"
            >
              <Arrow direction="left" /> <span className="hidden md:inline">Prev</span>
            </button>
            <button
              onClick={next}
              data-testid="lightbox-next"
              className="absolute right-32 md:right-32 top-1/2 -translate-y-1/2 text-paper hover:text-mist p-4 flex items-center gap-3 mono-label z-20"
            >
              <span className="hidden md:inline">Next</span> <Arrow direction="right" />
            </button>
            {/* Caption */}
            <div className="absolute bottom-4 left-4 right-4 md:right-36 text-center text-paper/80 mono-label">
              {orderedGallery[lightboxIdx].caption || " "}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Scroll-to-top affordance — fades in past ~60% of first viewport */}
      <AnimatePresence>
        {showTop && (
          <motion.button
            key="scroll-top"
            type="button"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={scrollToTop}
            aria-label="Scroll to top"
            data-testid="scroll-to-top"
            data-cursor="hover"
            className="fixed z-40 bottom-8 right-8 md:bottom-10 md:right-10 flex items-center gap-2 px-4 py-3 bg-paper text-ink border border-ink/25 mono-label hover:bg-marine hover:text-paper hover:border-marine transition-colors duration-500"
            style={{
              borderRadius: "999px",
              boxShadow: "0 12px 32px -14px rgba(14,17,19,0.28)",
            }}
          >
            <ArrowUp size={14} strokeWidth={1.4} aria-hidden />
            <span>Top</span>
          </motion.button>
        )}
      </AnimatePresence>
    </article>
  );
}
